import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip
} from '@mui/material';
import { 
  Mail, 
  Check, 
  Error as AlertCircle, 
  Refresh as RefreshCw 
} from '@mui/icons-material';

interface AuthStatus {
  authenticated: boolean;
  expires_at?: string;
  user_email: string;
  error?: string;
}

interface ProcessingStatus {
  processed_emails: number;
  total_records: number;
  latest_processing?: string;
}

interface ProcessingResult {
  success: boolean;
  message: string;
  records_processed: number;
  since_date: string;
}

const EmailProcessing: React.FC = () => {
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [sinceDays, setSinceDays] = useState(30);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
    getProcessingStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/outlook/auth/status');
      const status = await response.json();
      setAuthStatus(status);
    } catch (error) {
      console.error('Error checking auth status:', error);
      setAuthStatus({ authenticated: false, user_email: '', error: 'Failed to check status' });
    }
  };

  const getProcessingStatus = async () => {
    try {
      const response = await fetch('/api/emails/status');
      const status = await response.json();
      setProcessingStatus(status);
    } catch (error) {
      console.error('Error getting processing status:', error);
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setMessage(null);

    try {
      const response = await fetch('/api/outlook/auth/login', { method: 'POST' });
      const result = await response.json();

      if (result.success) {
        setMessage({ type: 'success', text: 'Authentication successful!' });
        await checkAuthStatus();
      } else {
        setMessage({ type: 'error', text: result.message || 'Authentication failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Authentication error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const handleProcessEmails = async () => {
    setProcessing(true);
    setMessage(null);

    try {
      const response = await fetch(`/api/emails/process?since_days=${sinceDays}`, { 
        method: 'POST' 
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          setMessage({ type: 'error', text: 'Not authenticated. Please login first.' });
          await checkAuthStatus();
          return;
        }
        throw new Error('Processing failed');
      }

      const result: ProcessingResult = await response.json();
      
      if (result.success) {
        setMessage({ 
          type: 'success', 
          text: `${result.message}. Processed ${result.records_processed} records since ${result.since_date}` 
        });
        await getProcessingStatus();
      } else {
        setMessage({ type: 'error', text: result.message || 'Processing failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Email processing error occurred' });
    } finally {
      setProcessing(false);
    }
  };

  const handleResetProcessing = async () => {
    if (!window.confirm('Are you sure you want to reset email processing status? This will allow reprocessing of all emails.')) {
      return;
    }

    try {
      const response = await fetch('/api/emails/reset', { method: 'DELETE' });
      const result = await response.json();
      
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        await getProcessingStatus();
      } else {
        setMessage({ type: 'error', text: 'Reset failed' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Reset error occurred' });
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isTokenExpiringSoon = (expiresAt?: string) => {
    if (!expiresAt) return false;
    const expires = new Date(expiresAt);
    const now = new Date();
    const oneHour = 60 * 60 * 1000;
    return (expires.getTime() - now.getTime()) < oneHour;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Email Processing
      </Typography>

      {message && (
        <Alert 
          severity={message.type === 'error' ? 'error' : 'success'} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Authentication Status */}
      <Card sx={{ mb: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mail />
              <Typography variant="h6">Microsoft Authentication</Typography>
            </Box>
          }
        />
        <CardContent>
          {authStatus ? (
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {authStatus.authenticated ? (
                  <Chip icon={<Check />} label="Authenticated" color="success" />
                ) : (
                  <Chip icon={<AlertCircle />} label="Not Authenticated" color="error" />
                )}
              </Box>
              
              {authStatus.user_email && (
                <Typography variant="body2">
                  Email: {authStatus.user_email}
                </Typography>
              )}
              
              {authStatus.expires_at && (
                <Typography 
                  variant="body2" 
                  color={isTokenExpiringSoon(authStatus.expires_at) ? 'warning.main' : 'text.primary'}
                >
                  Expires: {formatDate(authStatus.expires_at)}
                  {isTokenExpiringSoon(authStatus.expires_at) && ' (Soon!)'}
                </Typography>
              )}
              
              {authStatus.error && (
                <Typography variant="body2" color="error">
                  Error: {authStatus.error}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography>Loading authentication status...</Typography>
          )}

          <Button 
            onClick={handleLogin} 
            disabled={loading}
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Authenticating...' : 'Authenticate with Microsoft'}
          </Button>
        </CardContent>
      </Card>

      {/* Email Processing */}
      <Card>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RefreshCw />
              <Typography variant="h6">Email Processing</Typography>
            </Box>
          }
        />
        <CardContent>
          {processingStatus && (
            <Box sx={{ mb: 3 }}>
              <Stack direction="row" spacing={4} sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Processed Emails
                  </Typography>
                  <Typography variant="h6">
                    {processingStatus.processed_emails}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Total Records
                  </Typography>
                  <Typography variant="h6">
                    {processingStatus.total_records}
                  </Typography>
                </Box>
              </Stack>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Latest Processing
                </Typography>
                <Typography variant="body2">
                  {formatDate(processingStatus.latest_processing)}
                </Typography>
              </Box>
            </Box>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Process emails from last N days</InputLabel>
            <Select
              value={sinceDays}
              label="Process emails from last N days"
              onChange={(e) => setSinceDays(Number(e.target.value))}
            >
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={14}>14 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button 
              onClick={handleProcessEmails}
              disabled={processing || !authStatus?.authenticated}
              variant="contained"
              fullWidth
              startIcon={processing ? <CircularProgress size={20} /> : null}
            >
              {processing ? 'Processing...' : 'Process Emails'}
            </Button>

            <Button 
              onClick={handleResetProcessing}
              variant="outlined"
              disabled={processing}
            >
              Reset
            </Button>
          </Stack>

          <Button 
            onClick={() => {
              checkAuthStatus();
              getProcessingStatus();
            }}
            variant="outlined"
            fullWidth
            startIcon={<RefreshCw />}
          >
            Refresh Status
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmailProcessing;